import type { ComponentPropsWithoutRef, ReactNode } from 'react'

import { IconLink } from '@tabler/icons-react'

import { usePage } from '@subaio/components/src/Router/usePage'

import { extractElementText } from 'lib/extractElementText'
import { getNavigationId } from 'lib/getNavigationId'

import { Anchor } from './Anchor'
import { Heading1 } from './Heading1'
import { Heading2 } from './Heading2'
import { Heading3 } from './Heading3'
import { Heading4 } from './Heading4'
import { Heading5 } from './Heading5'

type Props = Omit<ComponentPropsWithoutRef<'h1'>, 'children'> & {
	children?: ReactNode
	level: number
	label?: string
	id?: string
	exposeId?: boolean
	prefix?: string
}

export const Heading = ({
	children,
	level,
	prefix,
	label = extractElementText(children),
	id = getNavigationId(label, prefix),
	exposeId = true,
	...rest
}: Props) => {
	const page = usePage()

	const defaultProps = {
		id: exposeId ? id : undefined,
		'aria-label': label,
		...rest,
	}

	if (!page) return null

	const defaultLinkProps = {
		name: page.name,
		params: page.params,
		fragment: id,
	}

	switch (level) {
		case 1:
			return (
				<Heading1 {...defaultProps}>
					{children}
					<Anchor {...defaultLinkProps}>
						<IconLink size={16} />
					</Anchor>
				</Heading1>
			)
		case 2:
			return (
				<Heading2 {...defaultProps}>
					{children}
					<Anchor {...defaultLinkProps}>
						<IconLink size={16} />
					</Anchor>
				</Heading2>
			)
		case 3:
			return (
				<Heading3 {...defaultProps}>
					{children}
					<Anchor {...defaultLinkProps}>
						<IconLink size={16} />
					</Anchor>
				</Heading3>
			)
		case 4:
			return (
				<Heading4 {...defaultProps}>
					{children}
					<Anchor {...defaultLinkProps}>
						<IconLink size={16} />
					</Anchor>
				</Heading4>
			)
		case 5:
			return (
				<Heading5 {...defaultProps}>
					{children}
					<Anchor {...defaultLinkProps}>
						<IconLink size={16} />
					</Anchor>
				</Heading5>
			)
	}

	return null
}
